import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ChevronRight } from '@mui/icons-material';
import { useAppNavigate } from '../../../Hooks/back/useAppNavigate';
import Icon from '../../../Assets/Icon/CustomIcon';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import Global from '../../../Services/Common/Global';

const MANUAL_BASE_PATH = 'files/Manuals/Mobile/';

export const MANUAL_FILE = {
  workStart: '[앱 사용 매뉴얼] 작업 시작, 종료.pdf',
  permit: '[앱 사용 메뉴얼] 안전작업허가서.pdf',
  workInfo: null,
  meeting: '[앱, 웹 사용 메뉴얼] 도급사업관리.pdf',
  tbm: '[앱 사용 매뉴얼] TBM.pdf',
  training: null,
  tbmCompletion: null,
  pendingApprovals: null,
  equipIssuance: null,
  equipStatus: null,
  notice: '[웹 사용 매뉴얼] 게시 및 공지.pdf',
  emergency: '[앱 사용 매뉴얼] 비상연락.pdf',
  emergencyManual: '[웹,앱 사용 매뉴얼] 비상대응매뉴얼.pdf',
  safetyCommittee: '[웹,앱 사용 매뉴얼] 산업안전보건위원회',
  signupLogin: '[웹,앱 사용 매뉴얼] 회원가입 및 로그인.pdf',
};

export function getManualPdfUrl(manualKey) {
  const fileName = MANUAL_FILE[manualKey];
  if (fileName == null || fileName === '') return null;
  const ext = fileName.split('.').pop()?.toLowerCase();
  const finalName = [
    'hwp',
    'doc',
    'docx',
    'ppt',
    'pptx',
    'xls',
    'xlsx',
  ].includes(ext)
    ? fileName.replace(/\.[^/.]+$/, '.pdf')
    : fileName;
  return `${Global.file.useUrl}${MANUAL_BASE_PATH}${encodeURIComponent(finalName)}`;
}

const groups = [
  {
    id: 'general',
    title: '공지 및 앱 이용 안내',
    items: [
      {
        manualKey: 'notice',
        label: '공지사항',
        icon: Icon.Campaign,
        iconColor: '#D97706',
        iconBg: '#FFFBEB',
      },
      {
        manualKey: 'emergency',
        label: '비상연락',
        icon: Icon.Siren,
        iconColor: '#DC2626',
        iconBg: '#FEF2F2',
      },
      {
        manualKey: 'emergencyManual',
        label: '비상대응메뉴얼',
        icon: Icon.Warning,
        iconColor: '#EA580C',
        iconBg: '#FFF7ED',
      },

      {
        manualKey: 'signupLogin',
        label: '회원가입 및 로그인',
        icon: Icon.UserLogin,
        iconColor: '#4F46E5',
        iconBg: '#EEF2FF',
      },
    ],
  },
  {
    id: 'safety',
    title: '안전 및 작업 관리',
    items: [
      {
        manualKey: 'workStart',
        label: '작업시작',
        icon: Icon.ToolTip,
        iconColor: '#0D9488',
        iconBg: '#F0FDFA',
      },
      {
        manualKey: 'permit',
        label: '작업허가서',
        icon: Icon.TaskAlt,
        iconColor: '#8B5CF6',
        iconBg: '#EDE9FE',
      },
      {
        manualKey: 'workInfo',
        label: '안전신문고',
        icon: Icon.Lightbulb,
        iconColor: '#3B82F6',
        iconBg: '#DBEAFE',
      },
      {
        manualKey: 'meeting',
        label: '회의록',
        icon: Icon.Description,
        iconColor: '#06B6D4',
        iconBg: '#ECFEFF',
      },
      {
        manualKey: 'safetyCommittee',
        label: '산업안전보건위원회',
        icon: Icon.Groups,
        iconColor: '#7C3AED',
        iconBg: '#F5F3FF',
      },
    ],
  },
  {
    id: 'tbm',
    title: 'TBM 및 교육',
    items: [
      {
        manualKey: 'tbm',
        label: 'TBM',
        icon: Icon.Groups,
        iconColor: '#10B981',
        iconBg: '#DCFCE7',
      },
      {
        manualKey: 'training',
        label: 'TBM 교육자료',
        icon: Icon.Groups,
        iconColor: '#0EA5E9',
        iconBg: '#E0F2FE',
      },
      {
        manualKey: 'tbmCompletion',
        label: 'TBM 이수현황',
        icon: Icon.FormatListBulleted,
        iconColor: '#16A34A',
        iconBg: '#DCFCE7',
      },
      {
        manualKey: 'pendingApprovals',
        label: '교육서명',
        icon: Icon.Description,
        iconColor: '#06B6D4',
        iconBg: '#ECFEFF',
      },
    ],
  },
  {
    id: 'ppe',
    title: '보호구 장비',
    items: [
      {
        manualKey: 'equipIssuance',
        label: '보호구 지급',
        icon: Icon.HardHat,
        iconColor: '#E11D48',
        iconBg: '#FFF1F2',
      },
      {
        manualKey: 'equipStatus',
        label: '보호구 지급현황',
        icon: Icon.Engineering,
        iconColor: '#BE185D',
        iconBg: '#FCE7F3',
      },
    ],
  },
];

function GuideRow({ item, onItemClick }) {
  const IconComponent = item.icon;
  return (
    <button
      type="button"
      onClick={() => onItemClick(item)}
      style={{ ['--row-accent']: item.iconColor }}
      className="group flex w-full items-center justify-between rounded-xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all duration-200 [webkit-tap-highlight-color:transparent] hover:shadow-md active:scale-[0.99]"
    >
      <div className="flex min-w-0 items-center gap-4">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-transform group-hover:scale-110"
          style={{ backgroundColor: item.iconBg }}
        >
          <IconComponent
            width={22}
            height={22}
            color={item.iconColor}
            className="shrink-0"
          />
        </div>
        <span className="text-lg font-semibold text-gray-800">
          {item.label}
        </span>
      </div>
      <ChevronRight
        className="!h-6 !w-6 shrink-0 text-gray-300 transition-colors group-hover:[color:var(--row-accent)]"
        fontSize="medium"
      />
    </button>
  );
}

export default function M_GUIDE_BASIC() {
  const dispatch = useDispatch();
  const { navigate } = useAppNavigate();

  const handleItemClick = useCallback(
    (item) => {
      const url = getManualPdfUrl(item.manualKey);
      if (url == null) {
        dispatch(showSnackbar({ message: '준비 중입니다', type: 'info' }));
        return;
      }
      navigate(`/M_Guide/manual/${encodeURIComponent(item.manualKey)}`, {
        state: { label: item.label },
      });
    },
    [dispatch, navigate]
  );

  return (
    <div className="min-h-full bg-[#F7F8FA] pb-8 [webkit-tap-highlight-color:transparent]">
      <main className="mx-auto max-w-3xl px-6 pb-8">
        {groups.map((section) => (
          <section key={section.id} className="mb-8">
            <h3 className="mb-3 px-1 text-sm font-bold tracking-wider text-gray-400 uppercase">
              {section.title}
            </h3>
            <div className="flex flex-col gap-3">
              {section.items.map((item) => (
                <GuideRow
                  key={item.manualKey}
                  item={item}
                  onItemClick={handleItemClick}
                />
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
